/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Camera, Upload, Sparkles, Loader2, Bandage, AlertTriangle, X, ImageIcon } from 'lucide-react';
import { listWoundCases, createWoundCase } from '@/lib/sprint4';
import { fileToBase64 } from '@/lib/sprint2';
import { BODY_REGIONS, WOUND_TYPES, StageBadge } from '@/components/woundcare/meta';

// Il dettaglio caso vive in pages/woundcare/WoundCaseDetailPage.jsx (re-export per le route esistenti)
export { WoundCaseDetailPage } from '@/pages/woundcare/WoundCaseDetailPage';

const WoundCareListPage = () => {
  const [cases, setCases] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState({ body_region: 'sacrum', wound_type_declared: 'pressure_ulcer', context: '', image_base64: '', mime_type: '', dataUrl: '' });
  const [creating, setCreating] = useState(false);
  const inputRef = useRef(null);
  const cameraRef = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const res = await listWoundCases();
      setCases(res.cases || []);
    } catch (e) {
      if (e?.response?.status !== 401) toast.error('Errore caricamento');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) { toast.error('Immagine troppo grande (max 6 MB)'); return; }
    const { base64, dataUrl, mime } = await fileToBase64(file);
    setForm((f) => ({ ...f, image_base64: base64, mime_type: mime || 'image/jpeg', dataUrl }));
  };

  const submit = async () => {
    if (!form.image_base64) { toast.error('Aggiungi una foto'); return; }
    setCreating(true);
    try {
      const c = await createWoundCase(form);
      toast.success('Case aperto — analisi AI iniziale in corso...');
      setOpenCreate(false);
      setForm({ body_region: 'sacrum', wound_type_declared: 'pressure_ulcer', context: '', image_base64: '', mime_type: '', dataUrl: '' });
      navigate(`/wound-care/${c.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore creazione case');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-6" data-testid="wound-list-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold flex items-center gap-2">
            <Bandage className="h-6 w-6 text-primary" /> Wound Care
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Carica foto di ferite/ulcere — l'AI (Claude 3.5 Vision) fa uno screening iniziale (staging NPUAP, essudato, red flag) e ti mette in contatto con un infermiere specialist.
          </p>
        </div>
        <Button onClick={() => setOpenCreate(true)} data-testid="wound-new-btn">
          <Plus className="h-4 w-4" /> Nuovo case
        </Button>
      </div>

      {!cases ? (
        <div className="grid gap-3 sm:grid-cols-2"><Skeleton className="h-32" /><Skeleton className="h-32" /></div>
      ) : cases.length === 0 ? (
        <Card><CardContent className="p-8 text-center space-y-3">
          <Bandage className="h-10 w-10 text-primary mx-auto" />
          <div className="text-sm">Nessun case attivo. Apri il tuo primo caso per una valutazione remota.</div>
          <Button onClick={() => setOpenCreate(true)}><Plus className="h-4 w-4" /> Nuovo case</Button>
        </CardContent></Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {cases.map((c) => (
            <Card key={c.id} className="cursor-pointer hover:border-primary/40 transition-colors" onClick={() => navigate(`/wound-care/${c.id}`)} data-testid={`wound-case-${c.id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold capitalize">{BODY_REGIONS.find((b) => b[0] === c.body_region)?.[1] || c.body_region}</div>
                  <Badge variant={c.status === 'open' ? 'default' : 'outline'} className="text-[10px]">{c.status === 'open' ? 'Attivo' : 'Chiuso'}</Badge>
                </div>
                <div className="text-xs text-muted-foreground">{c.photos_count} foto · aggiornato {new Date(c.updated_at).toLocaleDateString('it')}</div>
                {c.latest_analysis?.npuap_stage && c.latest_analysis.npuap_stage !== 'not_applicable' && (
                  <div><StageBadge stage={c.latest_analysis.npuap_stage} /></div>
                )}
                {c.latest_analysis?.needs_doctor_review && (
                  <div className="inline-flex items-center gap-1 text-[10px] font-bold text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
                    <AlertTriangle className="h-2.5 w-2.5" /> Valutazione medica consigliata
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="max-w-lg" data-testid="wound-new-dialog">
          <DialogHeader>
            <DialogTitle>Nuovo case wound care</DialogTitle>
            <DialogDescription>Foto chiara, ben illuminata, con la ferita al centro. Nessuna informazione personale visibile.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {form.dataUrl ? (
              <div className="relative">
                <img src={form.dataUrl} alt="Ferita" className="w-full max-h-56 object-contain rounded-lg border" data-testid="wound-preview" />
                <Button size="icon" variant="secondary" className="absolute top-2 right-2" onClick={() => setForm({ ...form, image_base64: '', mime_type: '', dataUrl: '' })}><X className="h-4 w-4" /></Button>
              </div>
            ) : (
              <>
                <Card className="p-4 border-dashed cursor-pointer text-center" onClick={() => inputRef.current?.click()} data-testid="wound-dropzone">
                  <ImageIcon className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                  <div className="text-sm">Carica foto della ferita</div>
                  <div className="text-[10px] text-muted-foreground">PNG / JPEG / WEBP · max 6 MB</div>
                </Card>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" onClick={() => inputRef.current?.click()}><Upload className="h-4 w-4" /> Galleria</Button>
                  <Button variant="outline" onClick={() => cameraRef.current?.click()}><Camera className="h-4 w-4" /> Fotocamera</Button>
                </div>
                <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
                <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
              </>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-muted-foreground">Zona corpo</label>
                <Select value={form.body_region} onValueChange={(v) => setForm({ ...form, body_region: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{BODY_REGIONS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Tipo ferita (opzionale)</label>
                <Select value={form.wound_type_declared} onValueChange={(v) => setForm({ ...form, wound_type_declared: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{WOUND_TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Contesto clinico (opzionale)</label>
              <Textarea rows={2} value={form.context} onChange={(e) => setForm({ ...form, context: e.target.value })} placeholder="es. paziente diabetico, 78 anni, prima medicazione dopo dimissione ospedaliera" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setOpenCreate(false)}>Annulla</Button>
            <Button onClick={submit} disabled={creating || !form.image_base64} data-testid="wound-create-btn">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Apri case + analisi AI
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WoundCareListPage;
